import {
  Button,
  Flex,
  Input,
  Modal,
  NumberInput,
  ScrollArea,
  SimpleGrid,
  Textarea,
  TextInput,
} from "@mantine/core";
import { SelectItem } from "@mantine/core/lib/Select/types";
import { DatePickerInput } from "@mantine/dates";
import { isNotEmpty, useForm } from "@mantine/form";
import { omit, uniqBy } from "lodash";
import { useCallback, useEffect, useState } from "react";

import { useTradeInsuranceCustomer } from "../../api/hooks/tradeInsurance/useTradeInsuranceCustomer.ts";
import { useTradeInsuranceNames } from "../../api/hooks/tradeInsurance/useTradeInsuranceNames.ts";
import Dropzone from "../../components/Dropzone/Dropzone.tsx";
import SvgCalendar from "../../components/Icons/Calendar.tsx";
import { Select } from "../../components/Select.tsx";
import {
  CreateTradeInsuranceInput,
  FileResult,
  OrderBy,
  TradeInsuranceStatus,
} from "../../graphql/generated.ts";
import { CustomerOption } from "../../types/customer.ts";
import {
  TradeInsurance,
  tradeInsuranceStatusOptions,
} from "../../types/tradeInsurance.ts";
import { getCompanyOptions } from "../../utils/company.ts";
import { parseStringToNumber, priceFormatter } from "../../utils/number.ts";

interface Props {
  data: TradeInsurance | null | undefined;
  customers: CustomerOption[];
  opened?: boolean;
  onClose: () => void;
  onSubmit: (value: CreateTradeInsuranceInput) => void;
}

const CreateTradeInsuranceModal = ({
  data,
  customers,
  opened = false,
  onClose,
  onSubmit,
}: Props) => {
  const customerOptions = getCompanyOptions(customers);

  const [tradeInsuranceNameOptions, setTradeInsuranceNameOptions] = useState<
    SelectItem[]
  >([]);

  const [{ data: insuranceNamesResponse, fetching: fetchingInsuranceNames }] =
    useTradeInsuranceNames({ orderBy: { insuranceName: OrderBy.Asc } });
  const insuranceNamesData = uniqBy(
    insuranceNamesResponse?.tradeInsurances.data ?? [],
    "insuranceName"
  );

  const form = useForm({
    validateInputOnBlur: true,
    initialValues: {
      insuranceName: data?.insuranceName ?? "",
      customerId: data?.customer.id && String(data.customer.id),
      approvedAmount: data?.approvedAmount ? Number(data.approvedAmount) : 0,
      creditLimitFees: data?.creditLimitFees ? Number(data.creditLimitFees) : 0,
      file: data?.file,
      grade: data?.grade ?? "",
      limitAmount: data?.limitAmount ? Number(data.limitAmount) : 0,
      notes: data?.notes,
      requestedAmount: data?.requestedAmount ? Number(data.requestedAmount) : 0,
      requestedDate: data?.requestedDate ? new Date(data.requestedDate) : null,
      resultDate: data?.resultDate ? new Date(data.resultDate) : null,
      status: data?.status ?? "",
      transactionsNumber: data?.transactionsNumber ?? 0,
    },
    validate: {
      insuranceName: isNotEmpty("Field is required"),
      customerId: isNotEmpty("Field is required"),
      creditLimitFees: isNotEmpty("Field is required"),
      grade: isNotEmpty("Field is required"),
      requestedAmount: isNotEmpty("Field is required"),
      requestedDate: isNotEmpty("Field is required"),
      status: isNotEmpty("Field is required"),
    },
  });

  const [
    { data: customerStatisticsResponse, fetching: fetchingCustomerStatistics },
  ] = useTradeInsuranceCustomer({
    customerId: form.values.customerId
      ? Number(form.values.customerId)
      : undefined,
    pause: Boolean(!form.values.customerId || data?.id),
  });
  const customerStatistics = customerStatisticsResponse?.tradeInsuranceCustomer;

  const handleClose = useCallback(() => {
    onClose();
    form.reset();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }
    const data = omit(form.values, "limitAmount", "transactionsNumber");
    onSubmit({
      ...data,
      status: form.values.status as TradeInsuranceStatus,
      file: form.values.file?.filePath,
      customerId: Number(form.values.customerId),
      creditLimitFees: String(form.values.creditLimitFees),
    } as CreateTradeInsuranceInput);
    handleClose();
  }, [form, handleClose, onSubmit]);

  const handleFileUpload = (files: FileResult[]) => {
    form.setValues({ file: files[0] });
  };

  const handleFileRemove = () => {
    form.setValues({ file: undefined });
  };

  useEffect(() => {
    if (data?.id) {
      return;
    }
    form.setFieldValue(
      "limitAmount",
      customerStatistics?.limitAmount
        ? Number(customerStatistics.limitAmount)
        : 0
    );
    form.setFieldValue(
      "transactionsNumber",
      customerStatistics?.transactionsNumber ?? 0
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerStatistics, data?.id]);

  useEffect(() => {
    if ((insuranceNamesData?.length ?? 0) <= tradeInsuranceNameOptions.length) {
      return;
    }
    const newTradeInsuranceNameOptions = insuranceNamesData.map((ti) => ({
      value: ti.insuranceName,
      label: ti.insuranceName,
    }));
    setTradeInsuranceNameOptions(newTradeInsuranceNameOptions);
  }, [insuranceNamesData, tradeInsuranceNameOptions.length]);

  return (
    <>
      <Modal
        opened={opened}
        title={data?.id ? "Edit Trade Insurance" : "Add Trade Insurance"}
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        size="xl"
        scrollAreaComponent={ScrollArea.Autosize}
        onClose={handleClose}
      >
        <Flex direction="column" gap={20}>
          <SimpleGrid
            cols={2}
            breakpoints={[{ maxWidth: "lg", cols: 1 }]}
            spacing={20}
            verticalSpacing={12}
          >
            <Flex direction="column" gap={12}>
              <Select
                data={tradeInsuranceNameOptions}
                loading={fetchingInsuranceNames}
                required
                searchable
                creatable
                label="Insurance Name"
                placeholder="Enter Insurance Name"
                size="m"
                getCreateLabel={(query) => `+ Create ${query}`}
                onCreate={(query: string) => {
                  const item = { value: query, label: query };
                  setTradeInsuranceNameOptions((prevValues) => [
                    ...prevValues,
                    item,
                  ]);
                  return item;
                }}
                {...form.getInputProps("insuranceName")}
              />
              <Select
                loading={!customers.length}
                size="m"
                required
                searchable
                label="Customer"
                placeholder="Select Customer"
                data={customerOptions}
                {...form.getInputProps("customerId")}
              />
              <NumberInput
                required
                precision={2}
                parser={parseStringToNumber}
                formatter={priceFormatter}
                hideControls
                label="Requested Trade Insurance Amount"
                placeholder="Enter Requested Trade Insurance Amount"
                size="m"
                {...form.getInputProps("requestedAmount")}
              />
              <NumberInput
                required
                precision={2}
                parser={parseStringToNumber}
                formatter={priceFormatter}
                hideControls
                label="Approved Trade Insurance Amount"
                placeholder="Enter Approved Trade Insurance Amount"
                size="m"
                {...form.getInputProps("approvedAmount")}
              />
              <NumberInput
                disabled
                precision={2}
                parser={parseStringToNumber}
                formatter={priceFormatter}
                hideControls
                label="Bababos Limit Amount"
                size="m"
                {...form.getInputProps("limitAmount")}
              />
              <DatePickerInput
                label="Requested Date"
                placeholder="Select Requested Date"
                required
                icon={<SvgCalendar />}
                firstDayOfWeek={0}
                size="m"
                valueFormat="YYYY MMM DD"
                {...form.getInputProps("requestedDate")}
              />
              <DatePickerInput
                label="Result Date"
                placeholder="Select Result Date"
                icon={<SvgCalendar />}
                firstDayOfWeek={0}
                size="m"
                valueFormat="YYYY MMM DD"
                clearable
                {...form.getInputProps("resultDate")}
              />
            </Flex>
            <Flex direction="column" gap={12}>
              <Select
                size="m"
                required
                label="Status"
                placeholder="Select Status"
                data={tradeInsuranceStatusOptions}
                {...form.getInputProps("status")}
              />
              <Input.Wrapper label="Trade Insurance File" size="m">
                <Dropzone
                  value={form.values.file ? [form.values.file] : null}
                  onUploadSuccess={handleFileUpload}
                  onRemove={handleFileRemove}
                />
              </Input.Wrapper>
              <TextInput
                required
                label="Grade"
                placeholder="Enter Grade"
                size="m"
                {...form.getInputProps("grade")}
              />
              <NumberInput
                required
                precision={2}
                parser={parseStringToNumber}
                formatter={priceFormatter}
                hideControls
                label="Credit Limit Fees"
                placeholder="Enter Credit Limit Fees"
                size="m"
                {...form.getInputProps("creditLimitFees")}
              />
              <NumberInput
                disabled
                precision={0}
                hideControls
                label="# of Transactions"
                size="m"
                parser={parseStringToNumber}
                formatter={(value) =>
                  priceFormatter(value, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                }
                min={0}
                {...form.getInputProps("transactionsNumber")}
              />
              <Textarea
                label="Notes"
                placeholder="Enter Notes"
                autosize
                size="m"
                minRows={3}
                maxRows={3}
                {...form.getInputProps("notes")}
              />
            </Flex>
          </SimpleGrid>
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={onClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              loading={fetchingCustomerStatistics}
              disabled={!form.isValid()}
              onClick={handleSubmit}
            >
              {data?.id ? "save" : "add"}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default CreateTradeInsuranceModal;
